import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Trash2, RotateCcw, Trash, ExternalLink, Loader2, Clock, FolderMinus, StickyNote, Link as LinkIcon, Images, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function TrashPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emptyOpen, setEmptyOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/trash");
      setEntries(r.data);
    } catch {
      toast.error("Could not load trash");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const onSynced = () => load();
    window.addEventListener("notelosh:synced", onSynced);
    return () => window.removeEventListener("notelosh:synced", onSynced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const restore = async (entry) => {
    try {
      await api.post(`/trash/${entry.id}/restore`);
      setEntries((prev) => prev.filter((x) => x.id !== entry.id));
      toast.success(
        entry.kind === "title"
          ? `Restored "${entry.name}" and its notes`
          : entry.kind === "group"
            ? `Restored all ${entry.items_count} ${entry.is_image ? "photos" : "files"}`
            : "Restored to its thread"
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not restore");
    }
  };

  const purge = async (entry) => {
    try {
      await api.delete(`/trash/${entry.id}`);
      setEntries((prev) => prev.filter((x) => x.id !== entry.id));
      toast.success("Deleted forever");
    } catch {
      toast.error("Could not delete");
    }
  };

  const emptyAll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/trash/empty");
      setEntries([]);
      toast.success(`Emptied trash (${r.data.deleted} items)`);
    } catch {
      toast.error("Could not empty trash");
    } finally {
      setBusy(false);
      setEmptyOpen(false);
    }
  };

  return (
    <div className="min-h-screen text-[#F3F4F6]">
      <header className="h-16 flex items-center gap-3 px-6 border-b border-[#1E1E1E] bg-[#0A0A0A]/60 backdrop-blur-md sticky top-0 z-10">
        <Button
          variant="ghost"
          size="icon"
          data-testid="trash-back-btn"
          onClick={() => navigate("/app")}
          className="h-8 w-8 rounded-full hover:bg-[#1E1E1E] text-[#9CA3AF]"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="font-heading text-xl font-bold flex items-center gap-2">
          <Trash2 className="h-5 w-5 text-[#3B82F6]" /> Trash
        </h1>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-[#6B7280] hidden sm:inline">Items auto-delete after 3 days</span>
          {entries.length > 0 && (
            <Button
              variant="ghost"
              data-testid="trash-empty-btn"
              onClick={() => setEmptyOpen(true)}
              className="h-8 rounded-full text-xs text-[#FCA5A5] hover:text-[#FCA5A5] hover:bg-[#7f1d1d]/30"
            >
              <Trash className="h-3.5 w-3.5 mr-1.5" /> Empty trash
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-[#6B7280]">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : entries.length === 0 ? (
          <div
            data-testid="trash-empty-state"
            className="rounded-2xl bg-[#121212]/70 backdrop-blur-md border border-dashed border-[#27272A] py-16 px-6 text-center"
          >
            <div className="mx-auto h-12 w-12 rounded-full bg-[#1E1E1E] border border-[#27272A] flex items-center justify-center mb-4">
              <Trash2 className="h-5 w-5 text-[#3B82F6]" />
            </div>
            <h4 className="font-heading text-lg font-semibold mb-1.5">Trash is empty</h4>
            <p className="text-sm text-[#9CA3AF] max-w-md mx-auto">
              Deleted notes land here and stick around for 3 days before they disappear for good.
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {entries.map((e) => (
              <TrashRow
                key={e.id}
                entry={e}
                onRestore={() => restore(e)}
                onPurge={() => purge(e)}
              />
            ))}
          </ul>
        )}
        <Link to="/app" className="inline-block mt-6 text-xs text-[#6B7280] hover:text-[#F3F4F6]">← Back to workspace</Link>
      </main>

      <AlertDialog open={emptyOpen} onOpenChange={setEmptyOpen}>
        <AlertDialogContent data-testid="trash-empty-dialog" className="bg-[#121212] border-[#27272A] text-[#F3F4F6]">
          <AlertDialogHeader>
            <AlertDialogTitle>Empty trash?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9CA3AF]">
              This permanently deletes everything in your trash. You can&#39;t undo this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trash-empty-cancel" className="bg-[#1E1E1E] border-[#27272A] text-[#F3F4F6] hover:bg-[#27272A]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="trash-empty-confirm"
              onClick={emptyAll}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {busy ? "Emptying…" : "Empty trash"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TrashRow({ entry, onRestore, onPurge }) {
  const isTitle = entry.kind === "title";
  const isGroup = entry.kind === "group";
  const groupNames = isGroup ? (entry.file_names || []).filter(Boolean) : [];
  const label = isTitle
    ? entry.name
    : isGroup
      ? `${entry.items_count} ${entry.is_image ? "photo" : "file"}${entry.items_count === 1 ? "" : "s"}` +
        (groupNames.length
          ? ` — ${groupNames.slice(0, 3).join(", ")}${groupNames.length > 3 ? ` +${groupNames.length - 3} more` : ""}`
          : "")
      : entry.type === "link"
        ? (entry.preview?.title || entry.url || "link")
        : entry.type === "file"
          ? (entry.file_name || "file")
          : (entry.content || "empty note").slice(0, 200);
  const IconEl = isTitle
    ? FolderMinus
    : isGroup
      ? (entry.is_image ? Images : Paperclip)
      : entry.type === "link" ? LinkIcon : StickyNote;
  return (
    <li
      data-testid={`trash-entry-${entry.id}`}
      className="flex items-start gap-3 rounded-xl bg-[#121212]/70 backdrop-blur-md border border-[#1E1E1E] hover:border-[#27272A] transition-colors p-3"
    >
      <div
        className="mt-1 h-7 w-7 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: (isTitle ? entry.color : entry.title_color) + "33" || "#3B82F633" }}
      >
        <IconEl
          className="h-3.5 w-3.5"
          style={{ color: isTitle ? entry.color : entry.title_color || "#3B82F6" }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[#F3F4F6] line-clamp-2 whitespace-pre-wrap break-words">{label}</div>
        <div className="flex items-center gap-2 text-[10px] text-[#6B7280] mt-1 flex-wrap">
          {isTitle ? (
            <>
              <span className="uppercase tracking-wider text-[#F59E0B]">Thread</span>
              <span>·</span>
              <span>{entry.items_count} note{entry.items_count === 1 ? "" : "s"} inside</span>
            </>
          ) : isGroup ? (
            <>
              <span className="uppercase tracking-wider text-[#F59E0B]">{entry.is_image ? "Photo set" : "File set"}</span>
              <span>·</span>
              <span>from &quot;{entry.title_name}&quot;</span>
            </>
          ) : (
            <>
              <span className="uppercase tracking-wider">{entry.type}</span>
              <span>·</span>
              <span>from &quot;{entry.title_name}&quot;</span>
            </>
          )}
          <span>·</span>
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{entry.days_remaining}d left</span>
        </div>
      </div>
      {entry.kind === "item" && entry.type === "link" && entry.url && (
        <a
          href={entry.url}
          target="_blank"
          rel="noopener noreferrer"
          className="h-8 w-8 rounded-full text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#1E1E1E] flex items-center justify-center shrink-0"
          aria-label="Open link"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
      <Button
        variant="ghost"
        size="sm"
        data-testid={`trash-restore-${entry.id}`}
        onClick={onRestore}
        className="h-8 rounded-full text-xs text-[#3B82F6] hover:text-[#60A5FA] hover:bg-[#1E1E1E]"
      >
        <RotateCcw className="h-3 w-3 mr-1" /> Restore
      </Button>
      <Button
        variant="ghost"
        size="icon"
        data-testid={`trash-purge-${entry.id}`}
        onClick={onPurge}
        title="Delete forever"
        className="h-8 w-8 rounded-full text-[#FCA5A5] hover:text-[#FCA5A5] hover:bg-[#7f1d1d]/30"
      >
        <Trash className="h-3.5 w-3.5" />
      </Button>
    </li>
  );
}
